import * as fullNameParser from 'parse-full-name';
import { put, takeEvery, select } from 'redux-saga/effects';
import * as appActions from 'Actions/app';
import {
  actionTypes,
  AppState,
  TutorialData,
  UserLevelData,
  LevelData,
  UnitData,
  TiersByLevels,
  SetAudioOn,
  SetRepMetronomeSound,
  SetRepDownbeatsSound,
  IUserData,
  SetColorblindType,
  UpdateUserData,
  CognitoUserEtc,
} from 'Types';
import { AxiosError } from 'axios';
import { getHighestCompletedLevel } from 'Utils/UserProgress';
import StartingULP from 'Fixtures/UserLevelProgress/StartingULP';
// import AxiosRetrier from 'Utils/AxiosRetrier';
import { UserTypes } from 'Utils/Constants';
import { awsRum } from 'Utils/AwsRum';
import axios from 'axios';
// import { Auth } from 'aws-amplify';
// import { getCurrentAuthToken, getCurrentUser } from 'Utils/Amplify';
import * as repertoireActions from 'Actions/repertoire';
import * as lessonActions from 'Actions/lesson';
import { RepertoireActionTypes } from 'Types/RepertoireTypes';
import { RepertoireData, UserRepertoireProgress } from 'Types/RepertoireTypes';

import { LessonState } from 'Reducers/lessonReducer';
import { gettimeSigForLevel } from './Lesson';
import { getCurrentAuthToken, getUser } from 'Utils/Auth';

// export const getCurrentUser = async () => {
//   const token = await getCurrentAuthToken()
//   if (!token) return null;
//   try {
//     return await authClient.fetchUserInfo(token);
//   } catch { return null; }
// };

// const axios = new AxiosRetrier()
const baseUrl = `${process.env.REACT_APP_BACKEND_URL}/api/v1/`;
interface unitsRequest {
  data: {
    data: UnitData[];
  };
}
interface levelsRequest {
  data: {
    data: LevelData[];
  };
}
interface userLevelProgressResponse {
  data: UserLevelData[];
}

interface tutorialsRequest {
  data: {
    data: TutorialData[];
  };
}

interface tiersByLevelsRequest {
  // data: TiersByLevels[]    <- Should be this, but server passes level_number as string
  data: {
    level_number: string;
    tiers: number[];
  }[];
}

interface repertoiresRequest {
  data: {
    data: RepertoireData[];
    hasNextPage: boolean;
  };
}

interface userDataRequest {
  data: IUserData;
}

interface userRepertoireProgressResponse {
  data: UserRepertoireProgress[];
}

interface singleUserRepertoireProgressResponse {
  data: UserRepertoireProgress;
}

function* onStartup(): Generator<unknown, void, unknown> {
  const lesson: LessonState = yield select(({ lessonReducer }) => lessonReducer);
  const data: AppState = yield select(({ mainAppReducer }) => mainAppReducer);
  const levelNumber = data.levelData[data.levelSelect]?.level_number;

  try {
    const jwtToken = yield getCurrentAuthToken();
    if (levelNumber) {
      const timeSig = yield gettimeSigForLevel(jwtToken, levelNumber, lesson.tierNumber);
      yield put(lessonActions.setLessonTimeSigNumerator(timeSig.numerator));
      yield put(lessonActions.setLessonTimeSigDenominator(timeSig.denominator));
    }
    if (jwtToken) {
      yield put(
        appActions.setUnitSelect({ unitSelect: 1 }), // this shouldn't be necessary - why is default state not set on load?
      );
    }

    yield put(
      lessonActions.updatePhrasesTilNextTier(4), // when refreshed, if on tier summary, it will call for tier summary on undefined
    );
  } catch {
    // intentionally empty
  }
}

function* updateLevelsData(): Generator<unknown, void, unknown> {
  const data: AppState = yield select(({ mainAppReducer }) => mainAppReducer);
  const lesson: LessonState = yield select(({ lessonReducer }) => lessonReducer);
  const jwtToken = yield getCurrentAuthToken();
  let levels: LevelData[] = [];
  const levelsResp = yield axios.get<{ data: LevelData[] }>(
    baseUrl + `levels?unitNumber=${data.unitSelect}&ascending=true`,
    { headers: { Authorization: `Bearer ${jwtToken}` } },
  );
  const LevelsResponse = levelsResp as levelsRequest;
  levels = levels.concat(LevelsResponse.data.data);

  yield put(
    appActions.fetchLevelDataSuccess({
      levelData: levels,
    }),
  );

  const TierCountByLevelResponse: tiersByLevelsRequest = yield axios.get<
    { level_number: string; tiers: number[] }[]
  >(baseUrl + `phrases/tiers`, {
    headers: { Authorization: `Bearer ${jwtToken}` },
  });
  // Passed from server with level_number as sring; so casting to into to match TiersByLevels type and easier use
  const TiersByLevels: TiersByLevels[] = TierCountByLevelResponse.data.map((v) => ({
    ...v,
    level_number: parseInt(v.level_number),
  }));
  yield put(appActions.updateTiersByLevels(TiersByLevels));
  const levelNumber = levels[data.levelSelect]?.level_number;
  const timeSig = yield gettimeSigForLevel(jwtToken, levelNumber, lesson.tierNumber);
  yield put(lessonActions.setLessonTimeSigNumerator(timeSig.numerator));
  yield put(lessonActions.setLessonTimeSigDenominator(timeSig.denominator));
}

function* onUnitSelect() {
  yield updateLevelsData();
}

function* onAuthStartup(action: unknown): Generator<unknown, void, unknown> {
  const data: AppState = yield select(({ mainAppReducer }) => mainAppReducer);
  let jwtToken: string | undefined;
  try {
    jwtToken = yield getCurrentAuthToken();
  } catch {
    // intentionally empty
  }
  if (jwtToken) {
    // First check UserLevelProgress, pull all rows for user
    const ulp_url = baseUrl + `user-level-progress`;
    let userLevelProgressResponse: userLevelProgressResponse;
    userLevelProgressResponse = yield axios.get<UserLevelData[]>(ulp_url, {
      headers: { Authorization: `Bearer ${jwtToken}` },
    });
    let userLevelProgress: UserLevelData[] = userLevelProgressResponse.data;
    // If user does not have a userLeverProgress, create a row for user
    if (Object.keys(userLevelProgress).length == 0) {
      const startingULP = StartingULP() as UserLevelData;
      try {
        userLevelProgressResponse = yield axios.post<UserLevelData>(ulp_url, startingULP, {
          headers: { Authorization: `Bearer ${jwtToken}` },
        });
        userLevelProgress = [userLevelProgressResponse.data as unknown as UserLevelData];
      } catch (err) {
        awsRum?.recordError(err);
        // sometimes this can happen due to a race condition, so this is just a bit of extra error handling
        if (
          err instanceof AxiosError &&
          err?.response?.data?.message === 'User level progress already exists for user/level'
        ) {
          userLevelProgressResponse = yield axios.get<UserLevelData>(ulp_url, {
            headers: { Authorization: `Bearer ${jwtToken}` },
          });
          userLevelProgress = userLevelProgressResponse.data;
          yield put(
            appActions.fetchUserLevelsSuccess({
              userLevelData: userLevelProgress,
            }),
          );
        }
      }
    } else {
      userLevelProgress = userLevelProgressResponse.data;
    }
    //Now set user Level data
    yield put(
      appActions.fetchUserLevelsSuccess({
        userLevelData: userLevelProgress,
      }),
    );

    // Then calculate the lastestLevelComplete for the user.
    const highestCompleteData = getHighestCompletedLevel(userLevelProgress);
    const lastLevelComplete = highestCompleteData.lastLevelComplete;

    // Now set highestLevelCompleted
    yield put(
      appActions.setHighestLevelCompleted({
        highestLevelCompleted: lastLevelComplete,
      }),
    );

    // Now that we have the highest level the user has completed, we will query all levels for corresponding unit
    // And lower. (Onces we have multiple units, new users won't have their roadmap populated with these levels yet)
    let _UnitsResponse: unitsRequest;
    // UnitsResponse = yield axios.get<any[]>(baseUrl+`units?levelNumber=${lastLevelComplete || 1}`, {headers: {'Authorization': `Bearer ${authToken}`}})

    // Now using the highest unit, grab all the levels up to and including that unit to populate the roadmap!
    let levels: LevelData[] = [];
    const LevelsResponse: levelsRequest = yield axios.get<{ data: LevelData[] }>(
      baseUrl + `levels?unitNumber=${data.unitSelect}&ascending=true`,
      { headers: { Authorization: `Bearer ${jwtToken}` } },
    );
    levels = levels.concat(LevelsResponse.data.data);

    yield put(
      appActions.fetchLevelDataSuccess({
        levelData: levels,
      }),
    );

    const TierCountByLevelResponse: tiersByLevelsRequest = yield axios.get<
      { level_number: string; tiers: number[] }[]
    >(baseUrl + `phrases/tiers`, {
      headers: { Authorization: `Bearer ${jwtToken}` },
    });
    // Passed from server with level_number as sring; so casting to into to match TiersByLevels type and easier use
    const TiersByLevels: TiersByLevels[] = TierCountByLevelResponse.data.map((v) => ({
      ...v,
      level_number: parseInt(v.level_number),
    }));
    yield put(appActions.updateTiersByLevels(TiersByLevels));
    const highestCompleteLevel = highestCompleteData.lastLevelComplete;
    yield getRepertoireData(highestCompleteLevel);

    // Get user Data:
    yield onUserDataRequest(action);

    // This was causing duplicate calling of the user data... don't need.
    // Will soon delete.

    // let user_data_url = baseUrl+`user-data`
    // let userDataResponse:userDataRequest
    // try {
    //   userDataResponse = yield axios.get<userDataRequest[]>(user_data_url, {headers: {'Authorization': `Bearer ${jwtToken}`}})
    //   if(userDataResponse.data.rep_downbeats_sound === null) userDataResponse.data.rep_downbeats_sound = true;
    //   if(userDataResponse.data.rep_metronome_sound === null) userDataResponse.data.rep_metronome_sound = true;
    //   yield put(
    //     appActions.setUserData(
    //       userDataResponse.data
    //     )
    //   )
    //   yield updateLevelsData()
    // } catch(err: any) {
    //   console.error(err)
    //   awsRum?.recordError(err)
    // }
  }
}

// Get user Data:
function* onUserDataRequest(_action: unknown): Generator<unknown, void, unknown> {
  // const user = yield getCurrentUser();
  // console.log("user?", user)
  let jwtToken: unknown;
  try {
    jwtToken = yield getCurrentAuthToken();
  } catch {
    // intentionally empty
  }

  const user_data_url = baseUrl + `user-data`;
  let userDataResponse: userDataRequest;
  try {
    userDataResponse = yield axios.get<userDataRequest[]>(user_data_url, {
      headers: { Authorization: `Bearer ${jwtToken}` },
    });
    // Set defaults to true if null or undefined
    if (userDataResponse.data.audio_on == null) userDataResponse.data.audio_on = true;
    if (userDataResponse.data.rep_downbeats_sound == null)
      userDataResponse.data.rep_downbeats_sound = true;
    if (userDataResponse.data.rep_metronome_sound == null)
      userDataResponse.data.rep_metronome_sound = true;
    yield put(appActions.setUserData(userDataResponse.data));
    yield updateLevelsData();
  } catch (err: unknown) {
    try {
      console.error(err);
      awsRum?.recordError(err);
      const user: CognitoUserEtc | undefined = yield getUser();
      if (user) {
        const NO_RETRY = {
          'axios-retry': { retries: 0, retryCondition: () => false } as {
            retries: number;
            retryCondition: () => boolean;
          },
        };
        userDataResponse = yield axios.post<IUserData>(
          user_data_url,
          {
            subscription_status: '', // Could be a new state (not yet active) or null. But something to differentiate from the stripe "upaid" "canceled" states
            user_type: UserTypes.USER.toString(),
            name: user?.attributes.name,
            email: user?.attributes.email,
            phone_number: user?.attributes.phone_number,
          },
          { headers: { Authorization: `Bearer ${jwtToken}`, ...NO_RETRY } },
        );
        // Set defaults to true if null or undefined for new users
        if (userDataResponse.data.audio_on == null) userDataResponse.data.audio_on = true;
        if (userDataResponse.data.rep_downbeats_sound == null)
          userDataResponse.data.rep_downbeats_sound = true;
        if (userDataResponse.data.rep_metronome_sound == null)
          userDataResponse.data.rep_metronome_sound = true;
        yield put(appActions.setUserData(userDataResponse.data));
        yield updateLevelsData();
      }
    } catch (innerErr: unknown) {
      console.log('Failed creating now?', innerErr);
    }
  }
}

function* onTutorialDataRequest(_action: unknown): Generator<unknown, void, unknown> {
  const jwtToken = yield getCurrentAuthToken() || '';

  const data: AppState = yield select(({ mainAppReducer }) => mainAppReducer);
  yield put(appActions.fetchTutorialDataProcessing(true));
  const levelNumber = data.levelData[data.levelSelect].level_number;
  const tutorialsResponse: tutorialsRequest = yield axios.get<{ data: TutorialData[] }>(
    baseUrl + `tutorials?levelNumber=${levelNumber}`,
    { headers: { Authorization: `Bearer ${jwtToken}` } },
  );

  const tutorialData = tutorialsResponse.data.data;
  yield put(
    appActions.fetchTutorialDataSuccess({
      tutorialData: tutorialData,
    }),
  );
  yield put(appActions.fetchTutorialDataProcessing(false));
  // let path = "/tutorial"
  // window.location.href = path // <- This is not idea solution, the push(path) should work, but doesn't
  // yield put(push(path))
}

function* onUpdateUserData(_action: UpdateUserData): Generator<unknown, void, unknown> {
  try {
    const appData: AppState = yield select(({ mainAppReducer }) => mainAppReducer);
    const userData: Partial<IUserData> = appData.userData || {};
    const copiedData = { ...userData };
    const jwtToken = yield getCurrentAuthToken();
    delete copiedData.user_id; // Don't send user_id to server
    const updatedUserData = yield axios.patch<IUserData>(baseUrl + `user-data`, copiedData, {
      headers: { Authorization: `Bearer ${jwtToken}` },
    });
    console.log('Updated user data:', updatedUserData.data);
    yield put(appActions.setUserData(updatedUserData.data));
  } catch (err) {
    awsRum?.recordError(err);
    console.error(err);
  }
}

function* onAudioUpdate(action: SetAudioOn): Generator<unknown, void, unknown> {
  // const { jwtToken }: AuthState = yield select(({authReducer}) => authReducer)
  try {
    const jwtToken = yield getCurrentAuthToken();
    const audioOn = action.payload.audioOn;
    yield axios.put<unknown>(
      baseUrl + `user-data/set-user-audio?audio_on=${audioOn}`,
      {},
      {
        headers: { Authorization: `Bearer ${jwtToken}` },
      },
    );
    yield put(appActions.updateUserData({ audio_on: audioOn }));
  } catch (err) {
    awsRum?.recordError(err);
    console.error(err);
  }
}

function* onColorblindTypeUpdate(action: SetColorblindType): Generator<unknown, void, unknown> {
  try {
    const jwtToken = yield getCurrentAuthToken();
    const colorblindType = action.payload;
    yield axios.put<unknown>(
      baseUrl + `user-data/set-colorblind-type?colorblind_type=${colorblindType}`,
      {},
      {
        headers: { Authorization: `Bearer ${jwtToken}` },
      },
    );
    yield put(appActions.updateUserData({ colorblind_type: colorblindType }));
  } catch (err) {
    awsRum?.recordError(err);
    console.error(err);
  }
}

function* onsetLastShownSurvey(action: unknown): Generator<unknown, void, unknown> {
  // const { jwtToken }: AuthState = yield select(({authReducer}) => authReducer)
  try {
    const jwtToken = yield getCurrentAuthToken();
    const lastShownSurvey = (
      action as { payload: { last_shown_survey: Date } }
    ).payload.last_shown_survey.toISOString();
    yield axios.put<unknown>(
      baseUrl + `user-data/set-last-shown-survey?last_shown_survey=${lastShownSurvey}`,
      {},
      {
        headers: { Authorization: `Bearer ${jwtToken}` },
      },
    );
    yield put(appActions.updateUserData({ last_shown_survey: lastShownSurvey }));
  } catch (err) {
    awsRum?.recordError(err);
    console.error(err);
  }
}

function* onUpdateMailchimpInfo(action: unknown): Generator<unknown, void, unknown> {
  // const { jwtToken }: AuthState = yield select(({authReducer}) => authReducer)
  try {
    const jwtToken = yield getCurrentAuthToken();
    const payload = (action as { payload: { signUpData: { name: string; email: string } } })
      .payload;
    const fullName = fullNameParser.parseFullName(payload.signUpData.name);
    const firstName = fullName.first ? fullName.first : fullName.last;
    const lastName = fullName.first && fullName.last ? fullName.last : undefined;
    yield axios.post<unknown>(
      baseUrl + `mailchimp/add-to-list`,
      {
        first_name: firstName,
        last_name: lastName,
        email: payload.signUpData.email,
      },
      {
        headers: { Authorization: `Bearer ${jwtToken}` },
      },
    );
  } catch (err) {
    awsRum?.recordError(err);
    console.error(err);
  }
}

function* onGetSubscriptionStatus(_action: unknown): Generator<unknown, void, unknown> {
  const jwtToken = yield getCurrentAuthToken();
  try {
    const userDataResponse = yield axios.get<IUserData>(baseUrl + `user-data`, {
      headers: { Authorization: `Bearer ${jwtToken}` },
    });
    console.log('putting subscription status:', userDataResponse.data.subscription_status);
    yield put(appActions.setSubscriptionStatus(userDataResponse.data.subscription_status));
  } catch (err) {
    awsRum?.recordError(err);
    console.error(err);
  }
}

function* getRepertoireData(highestCompleteLevel: number): Generator<unknown, void, unknown> {
  const urp_url = baseUrl + `user-repertoire-progress`;
  const jwtToken = yield getCurrentAuthToken();
  try {
    const userRepertoireProgressResponse: userRepertoireProgressResponse = yield axios.get<
      UserRepertoireProgress[]
    >(urp_url, {
      headers: { Authorization: `Bearer ${jwtToken}` },
    });
    if (Object.keys(userRepertoireProgressResponse.data).length) {
      yield put(repertoireActions.fetchURPSuccess(userRepertoireProgressResponse.data));
    }

    // Get Repertoire by Highest Level Complete
    if (highestCompleteLevel > 0) {
      // intentionally empty
    }

    // Get ALL repertoires:
    let repertoires: RepertoireData[] = [];
    let RepertoiresResponse: repertoiresRequest;

    let page = 1;
    let hasNextPage = true;
    while (hasNextPage) {
      RepertoiresResponse = yield axios.get<{
        data: RepertoireData[];
        hasNextPage: boolean;
      }>(baseUrl + `repertoires`, {
        headers: { Authorization: `Bearer ${jwtToken}` },
        params: { page },
      });
      repertoires = repertoires.concat(RepertoiresResponse.data.data);
      hasNextPage = RepertoiresResponse.data.hasNextPage;
      if (hasNextPage) {
        page += 1;
      }
    }

    // Instead of sorting here, could sort relations by asc backen..
    repertoires.forEach((rep) => {
      rep.composers.sort((a, b) => a.id - b.id);
      rep.genres.sort((a, b) => a.id - b.id);
    });
    yield put(repertoireActions.fetchRepertoireDataSuccess(repertoires));
  } catch (err) {
    if (err instanceof AxiosError && err?.response?.status && err.response.status > 499) {
      awsRum?.recordError(err);
    } else if (!(err instanceof AxiosError)) {
      awsRum?.recordError(err);
    }
  }
}

function* onUserRepertoireProgressUpdate(action: {
  payload: UserRepertoireProgress;
}): Generator<unknown, void, unknown> {
  try {
    const jwtToken = yield getCurrentAuthToken();
    const urp_url = baseUrl + `user-repertoire-progress`;
    const userRepertoireProgressResponse: singleUserRepertoireProgressResponse =
      yield axios.put<UserRepertoireProgress>(urp_url, action.payload, {
        headers: { Authorization: `Bearer ${jwtToken}` },
      });
    yield put(repertoireActions.fetchURPSuccess([userRepertoireProgressResponse.data]));
  } catch (err) {
    if (err instanceof AxiosError && err?.response?.status && err.response.status > 499) {
      awsRum?.recordError(err);
    } else if (!(err instanceof AxiosError)) {
      awsRum?.recordError(err);
    }
  }
}

function* onRepMetronomeSoundUpdate(
  action: SetRepMetronomeSound,
): Generator<unknown, void, unknown> {
  try {
    const jwtToken = yield getCurrentAuthToken();
    const repMetronomeSound = action.payload;

    yield axios.put<unknown>(
      baseUrl + `user-data/set-rep-metronome-sound?rep_metronome_sound=${repMetronomeSound}`,
      {},
      {
        headers: { Authorization: `Bearer ${jwtToken}` },
      },
    );
    yield put(appActions.updateUserData({ rep_metronome_sound: repMetronomeSound }));
  } catch (err) {
    if (err instanceof AxiosError && err?.response?.status && err.response.status > 499) {
      awsRum?.recordError(err);
    } else if (!(err instanceof AxiosError)) {
      awsRum?.recordError(err);
    }
  }
}

function* onRepDownbeatsSoundUpdate(
  action: SetRepDownbeatsSound,
): Generator<unknown, void, unknown> {
  const jwtToken = yield getCurrentAuthToken();

  const repDownbeatsSound = action.payload;
  try {
    yield axios.put<unknown>(
      baseUrl + `user-data/set-rep-downbeats-sound?rep_downbeats_sound=${repDownbeatsSound}`,
      {},
      {
        headers: { Authorization: `Bearer ${jwtToken}` },
      },
    );
    yield put(appActions.updateUserData({ rep_downbeats_sound: repDownbeatsSound }));
  } catch (err) {
    if (err instanceof AxiosError && err?.response?.status && err.response.status > 499) {
      awsRum?.recordError(err);
    } else if (!(err instanceof AxiosError)) {
      awsRum?.recordError(err);
    }
  }
}

export default [
  takeEvery(RepertoireActionTypes.UPDATE_REPERTOIRE_DATA_REQUEST, onUserRepertoireProgressUpdate),
  takeEvery(actionTypes.FETCH_TUTORIAL_DATA, onTutorialDataRequest),
  takeEvery(actionTypes.AUTHENTICATED_STARTUP, onAuthStartup),
  takeEvery(actionTypes.USER_DATA_REQUEST, onUserDataRequest),
  takeEvery(actionTypes.SET_UNIT_SELECT, onUnitSelect),
  takeEvery(actionTypes.SET_AUDIO_ON, onAudioUpdate),
  takeEvery(actionTypes.SET_COLORBLIND_TYPE, onColorblindTypeUpdate),
  takeEvery(actionTypes.UPDATE_MAILCHIMP_INFO, onUpdateMailchimpInfo),
  takeEvery(actionTypes.SET_LAST_SHOWN_SURVEY, onsetLastShownSurvey),
  takeEvery(actionTypes.GET_SUBSCRIPTION_STATUS, onGetSubscriptionStatus),
  takeEvery(actionTypes.SET_REP_METRONOME_SOUND, onRepMetronomeSoundUpdate),
  takeEvery(actionTypes.SET_REP_DOWNBEATS_SOUND, onRepDownbeatsSoundUpdate),
  takeEvery(actionTypes.UPDATE_USER_DATA, onUpdateUserData),
  onStartup(),
];
